import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { createSubMeadiaSource } from "../Services/APIService";

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
}

interface CreateSubMediaSourceProps {
  mediaSources: MediaSource[];
  onCreateComplete: (data: SubMediaSource) => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  mediaSourceID: number | null;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  mediaSourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: Date;
  isAssignTabShown?: boolean;
}

const CreateSubMediaSource: React.FC<CreateSubMediaSourceProps> = ({
  mediaSources,
  onCreateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    mediaSourceID: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMediaSources, setIsLoadingMediaSources] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mediaSources.length > 0) {
      setFormData((prev) => ({
        ...prev,
        mediaSourceID: mediaSources[0].sourceId,
      }));
      setIsLoadingMediaSources(false);
    }
  }, [mediaSources]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "mediaSourceID") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? null : parseInt(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleCancel = () => {
    setFormData({ name: "", mediaSourceID: null });
    setError(null);
    onCancel();
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Please enter a valid SubMediaSource Name");
      return;
    }

    if (formData.mediaSourceID === null) {
      setError("Please select a Media Source");
      return;
    }

    try {
      setIsLoading(true);

      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      const payload = {
        name: formData.name.trim().replace(/\s+/g, " "),
        mediaSourceID: formData.mediaSourceID,
        tenantId: parseInt(tenantId),
        createdBy: parseInt(userId),
      };

      const result = await createSubMeadiaSource(payload);
      console.log("Create SubMediaSource result:", result);
      if (result.isCreated) {
        const mappedData: SubMediaSource = {
          id: result.data.id,
          name: result.data.name,
          mediaSourceID: result.data.mediA_SOURCE_ID,
          mediaSourceName: "",
          isActive: result.data.iS_ACTIVE,
          createdBy: result.data.createD_BY,
          createdOn: result.data.createD_ON,
        };
        toast.success(result.message || "SubMediaSource Created Successfully!");
        setFormData({
          name: "",
          mediaSourceID:
            mediaSources.length > 0 ? mediaSources[0].sourceId : null,
        });
        onCreateComplete(mappedData);
      } else {
        throw new Error(result.message || "Failed to create SubMediaSource");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <div className="card mb-4">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create Sub Media Source</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="mediaSourceID" className="form-label">
                Media Source Name{requiredField}
              </label>
              {isLoadingMediaSources ? (
                <div className="text-muted">Loading Media Source...</div>
              ) : (
                <select
                  id="mediaSourceID"
                  name="mediaSourceID"
                  className="form-select form-control-lg"
                  value={formData.mediaSourceID ?? ""}
                  onChange={handleInputChange}
                  required
                >
                  {mediaSources.filter((source) => source.isActive === true)
                    .length === 0 ? (
                    <option value="" disabled>
                      No media sources available
                    </option>
                  ) : (
                    mediaSources
                      .filter((source) => source.isActive === true)
                      .map((source) => (
                        <option key={source.sourceId} value={source.sourceId}>
                          {source.sourceName}
                        </option>
                      ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="name" className="form-label">
                Sub Media Source Name{requiredField}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control form-control-lg"
                placeholder="Enter Sub Media Source Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-12 mt-3">
              <button
                type="submit"
                className="btn btn-primary  me-2"
                disabled={isLoading || isLoadingMediaSources}
              >
                {isLoading ? "Creating..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSubMediaSource;
